import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const { isSupervisor } = useAuth();
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const endpoint = isSupervisor ? '/reports/dashboard' : '/reports/my-dashboard';
    api.get(endpoint)
      .then(r => setStats(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isSupervisor]);

  if (loading) return (
    <div className="text-center py-5">
      <div className="spinner-border text-primary" />
    </div>
  );

  return isSupervisor
    ? <AdminDashboard stats={stats} />
    : <EmployeeDashboard stats={stats} />;
}

// ── Employee Dashboard ────────────────────────────────────────
function EmployeeDashboard({ stats }) {
  return (
    <>
      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-md-3">
          <Link to="/courses" className="text-decoration-none">
            <div className="stat-card">
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <div className="text-muted mb-1" style={{ fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>My Courses</div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0f172a' }}>{stats?.total_courses ?? 0}</div>
                  <div style={{ fontSize: '.75rem', color: '#64748b' }}>{stats?.overall_progress ?? 0}% completed</div>
                </div>
                <div className="icon-box bg-primary-subtle">
                  <i className="bi bi-book-fill text-primary" />
                </div>
              </div>
            </div>
          </Link>
        </div>
        <div className="col-sm-6 col-md-3">
          <Link to="/attendance" className="text-decoration-none">
            <div className="stat-card">
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <div className="text-muted mb-1" style={{ fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>Attendance</div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0f172a' }}>{stats?.attendance_pct ?? 0}%</div>
                  <div style={{ fontSize: '.75rem', color: '#64748b' }}>This month</div>
                </div>
                <div className="icon-box bg-info-subtle">
                  <i className="bi bi-calendar-check-fill text-info" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Monthly Report flow */}
      <div className="content-section mb-4">
        <h6 className="mb-3">
          <i className="bi bi-file-earmark-text me-2" />Monthly Status Report
        </h6>
        <p className="text-muted mb-3" style={{ fontSize: '.875rem' }}>
          Every month, fill out and submit your Status Report. Your admin will review it and approve or send it back.
        </p>
        <div className="d-flex align-items-center gap-0 mb-3 flex-wrap" style={{ fontSize: '.8125rem' }}>
          <FlowStep icon="bi-pencil-square" label="You fill the form" active />
          <FlowArrow />
          <FlowStep icon="bi-send" label="Submit for review" />
          <FlowArrow />
          <FlowStep icon="bi-clipboard-check" label="Admin approves" />
          <FlowArrow />
          <FlowStep icon="bi-arrow-repeat" label="Synced to Compliance Portal" />
        </div>
        <Link to="/psr-report" className="btn btn-primary btn-sm">
          <i className="bi bi-plus-lg me-2" />Open Monthly Report
        </Link>
      </div>

      {/* How courses work */}
      <div className="content-section">
        <h6 className="mb-2"><i className="bi bi-info-circle me-2" />How Courses Work</h6>
        <ul className="mb-0" style={{ fontSize: '.8125rem', color: '#64748b', paddingLeft: '1.25rem' }}>
          <li className="mb-1">Complete each section in order — the next day unlocks after you finish the current one.</li>
          <li className="mb-1">Pass the quiz at the end of each day to mark it complete.</li>
          <li>Your progress is tracked automatically.</li>
        </ul>
      </div>
    </>
  );
}

function FlowStep({ icon, label, active }) {
  return (
    <div className="d-flex flex-column align-items-center text-center px-2" style={{ minWidth: 90 }}>
      <div className="d-flex align-items-center justify-content-center rounded-3 mb-1"
        style={{
          width: 36, height: 36,
          background: active ? '#2563eb' : '#f1f5f9',
          color:      active ? '#fff'    : '#64748b',
        }}>
        <i className={`bi ${icon}`} style={{ fontSize: '1rem' }} />
      </div>
      <span style={{ fontSize: '.6875rem', color: active ? '#2563eb' : '#64748b', fontWeight: active ? 600 : 400 }}>
        {label}
      </span>
    </div>
  );
}

function FlowArrow() {
  return <i className="bi bi-chevron-right text-muted d-none d-sm-block" style={{ fontSize: '.75rem', marginTop: -10 }} />;
}

// ── Admin Dashboard ───────────────────────────────────────────
function AdminDashboard({ stats }) {
  const topCards = [
    {
      label: 'Total Employees',
      value: stats?.total_employees ?? 0,
      sub: 'registered users',
      icon: 'bi-people-fill', bg: 'bg-primary-subtle', color: 'text-primary',
      link: '/users',
    },
    {
      label: 'Active Courses',
      value: stats?.total_courses ?? 0,
      sub: `${stats?.active_learners ?? 0} active learners`,
      icon: 'bi-book-fill', bg: 'bg-success-subtle', color: 'text-success',
      link: '/courses',
    },
    {
      label: 'Reports Pending',
      value: stats?.avg_psr ?? 0,
      sub: 'awaiting your review',
      icon: 'bi-clipboard-check', bg: 'bg-warning-subtle', color: 'text-warning',
      link: '/psr-review',
      urgent: (stats?.avg_psr ?? 0) > 0,
    },
    {
      label: 'Completions',
      value: stats?.completions ?? 0,
      sub: 'course completions',
      icon: 'bi-patch-check-fill', bg: 'bg-info-subtle', color: 'text-info',
      link: '/reports',
    },
  ];

  return (
    <>
      {/* Stat cards */}
      <div className="row g-3 mb-4">
        {topCards.map(c => (
          <div className="col-sm-6 col-md-3" key={c.label}>
            <Link to={c.link} className="text-decoration-none">
              <div className={`stat-card${c.urgent ? ' border-warning' : ''}`}
                style={c.urgent ? { borderColor: '#f59e0b', borderWidth: 1.5 } : {}}>
                <div className="d-flex align-items-start justify-content-between">
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>
                      {c.label}
                    </div>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: c.urgent ? '#d97706' : '#0f172a' }}>
                      {c.value}
                    </div>
                    <div style={{ fontSize: '.75rem', color: '#64748b' }}>{c.sub}</div>
                  </div>
                  <div className={`icon-box ${c.bg}`}>
                    <i className={`bi ${c.icon} ${c.color}`} />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* PSR review CTA — highlighted when pending */}
      {(stats?.avg_psr ?? 0) > 0 && (
        <div className="mb-4 p-3 rounded-3 d-flex align-items-center justify-content-between gap-3"
          style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-circle-fill" style={{ color: '#d97706', fontSize: '1.1rem' }} />
            <div>
              <span className="fw-semibold" style={{ fontSize: '.875rem', color: '#92400e' }}>
                {stats.avg_psr} monthly report{stats.avg_psr !== 1 ? 's' : ''} waiting for your approval
              </span>
              <div style={{ fontSize: '.75rem', color: '#b45309' }}>
                Employees have submitted their reports — review and approve to sync with the Compliance Portal.
              </div>
            </div>
          </div>
          <Link to="/psr-review" className="btn btn-sm fw-semibold flex-shrink-0"
            style={{ background: '#d97706', color: '#fff', borderColor: '#d97706' }}>
            Review Now
          </Link>
        </div>
      )}

      {/* Quick Actions */}
      <div className="row g-3">
        <div className="col-md-6">
          <div className="content-section">
            <h6 className="mb-3"><i className="bi bi-lightning-fill me-2" />Quick Actions</h6>
            <div className="d-flex flex-column gap-2">
              <Link to="/users" className="btn btn-outline-primary btn-sm text-start">
                <i className="bi bi-person-plus me-2" />Add New Employee
              </Link>
              <Link to="/course-builder" className="btn btn-outline-success btn-sm text-start">
                <i className="bi bi-plus-circle me-2" />Create New Course
              </Link>
              <Link to="/psr-review" className="btn btn-outline-secondary btn-sm text-start">
                <i className="bi bi-clipboard-check me-2" />Review Monthly Reports
              </Link>
              <Link to="/csv-import" className="btn btn-outline-secondary btn-sm text-start">
                <i className="bi bi-upload me-2" />Import Attendance CSV
              </Link>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="content-section">
            <h6 className="mb-3"><i className="bi bi-arrow-repeat me-2" />PSR Flow (how it works)</h6>
            <ol className="mb-0" style={{ fontSize: '.8125rem', color: '#64748b', paddingLeft: '1.25rem' }}>
              <li className="mb-2">Employee fills their <strong>Monthly Report</strong> in the LMS portal.</li>
              <li className="mb-2">They submit it — status changes to <span className="badge bg-warning text-dark">Submitted</span></li>
              <li className="mb-2">You review it here → approve or reject.</li>
              <li>On approval, data <strong>automatically syncs</strong> to the Compliance Portal employee profile.</li>
            </ol>
          </div>
        </div>
      </div>
    </>
  );
}
